import { Link } from "react-router-dom";

import { money, pct, timeAgo } from "../format";
import { Avatar } from "../shell/Avatar";
import { SimBadge } from "./social";

export interface ActivityRow {
  id: number;
  verb: string;
  target_type: string;
  target_id: string;
  target_title: string;
  detail: Record<string, unknown>;
  created_at: string;
  actor_username: string | null;
  actor_name: string | null;
  actor_avatar: string | null;
  actor_simulated: boolean | null;
  read_at?: string | null;
}

export function targetHref(type: string, id: string): string {
  switch (type) {
    case "strategy":
      return `/s/${id}`;
    case "run":
      return `/r/${id}`;
    case "bot":
      return `/b/${id}`;
    case "user":
      return `/u/${id}`;
    case "season":
      return `/league/${id}`;
    default:
      return "/";
  }
}

const STATUS: Record<string, string> = {
  under_review: "under review",
  in_progress: "in progress",
  wont_fix: "won't fix",
};

/** One line of the activity feed, as a sentence a person would say. */
function sentence(a: ActivityRow): { text: string; extra?: string; tone?: "up" | "down" } {
  const d = a.detail ?? {};
  switch (a.verb) {
    case "published":
      return { text: a.target_type === "run" ? "published a benchmark" : "published a strategy" };
    case "released":
      return { text: `released version ${String(d.version ?? "")} of` };
    case "rated":
      return {
        text: `rated ${"★".repeat(Number(d.stars ?? 0))}`,
        extra: d.review ? `“${String(d.review)}”` : undefined,
      };
    case "commented":
      return { text: "commented on", extra: d.excerpt ? `“${String(d.excerpt)}”` : undefined };
    case "replied":
      return { text: "replied in", extra: d.excerpt ? `“${String(d.excerpt)}”` : undefined };
    case "forked":
      return { text: "forked" };
    case "copied_bot":
      return { text: "started a copy of the bot" };
    case "followed":
      return { text: "started following" };
    case "saved":
      return { text: "saved" };
    case "badge_granted":
      return { text: `approved the ✓ Verified badge (v${String(d.version ?? "")}) for`, tone: "up" };
    case "badge_rejected":
      return { text: "reviewed the verification request for", extra: d.note ? String(d.note) : undefined };
    case "badge_revoked":
      return { text: "removed a badge from", extra: d.reason ? String(d.reason) : undefined };
    case "made_official":
      return { text: "published a ★ Official copy of" };
    case "season_finished":
      if (d.rank === null)
        return { text: `${String(d.bot ?? "Your bot")} closed no trade, so it was not ranked, in` };
      return {
        text: `${String(d.bot ?? "Your bot")} finished #${String(d.rank ?? "?")} of ${String(d.of ?? "")} in`,
        tone: d.rank === 1 ? "up" : undefined,
      };
    case "verified":
      return { text: "re-ran and verified ✓", tone: "up" };
    case "failed_verify":
      return { text: "re-ran and could not reproduce", tone: "down" };
    case "deployed":
      return { text: `deployed a paper bot on ${String(d.symbol ?? "")} ${String(d.timeframe ?? "")}:` };
    case "opened_position":
      return {
        text: `opened a ${String(d.side ?? "")} position${d.price ? ` at ${money(Number(d.price), 2)}` : ""} —`,
      };
    case "closed_trade": {
      const p = Number(d.pnlPct ?? 0) * 100;
      return { text: `closed a trade ${pct(p, 2)} —`, tone: p >= 0 ? "up" : "down" };
    }
    case "status_changed":
      return {
        text: `marked as ${STATUS[String(d.to)] ?? String(d.to)}:`,
        extra: d.note ? String(d.note) : undefined,
      };
    case "new_bug":
      return { text: "reported a bug" };
    case "new_feedback":
      return { text: "sent feedback" };
    case "voted":
      return { text: "voted for" };
    default:
      return { text: a.verb.replace(/_/g, " ") };
  }
}

export function ActivityItem({ a, compact = false }: { a: ActivityRow; compact?: boolean }) {
  const s = sentence(a);
  return (
    <li className={`activity${a.read_at === null ? " is-unread" : ""}`}>
      {a.actor_avatar && <Avatar seed={a.actor_avatar} size={compact ? 22 : 28} />}
      <div className="activity__body">
        <p>
          {a.actor_username ? (
            <Link to={`/u/${a.actor_username}`} className="activity__actor">
              {a.actor_name}
            </Link>
          ) : (
            <strong>Someone</strong>
          )}
          {a.actor_simulated && <SimBadge />} <span className={s.tone ?? ""}>{s.text}</span>{" "}
          <Link to={targetHref(a.target_type, a.target_id)}>{a.target_title || a.target_type}</Link>
        </p>
        {s.extra && !compact && <p className="activity__extra muted">{s.extra}</p>}
      </div>
      <time className="muted small" dateTime={a.created_at} title={new Date(a.created_at).toLocaleString()}>
        {timeAgo(a.created_at)}
      </time>
    </li>
  );
}
